import { Injectable, inject } from '@angular/core';
import {
  CreateTimeSeriesDataPointDto,
  isValidFiniteNumber,
  TimeSeriesFieldDataType,
} from '@anessa/shared-models';
import { CsvSeparator } from '../../../shared/services/file-export/file-export.service';
import { FileImportService } from '../../../shared/services/file-import/file-import.service';
import { Utils } from '../../../shared/utils';
import {
  TimeSeriesTable,
  TimeSeriesTableColumn,
} from '../models/timeseries-table.model';
import { slugifyColumnKey } from '../timeseries-column-key.util';
import { TimeSeriesValidationService } from './timeseries-validation.service';

export interface TimeSeriesImportError {
  row: number;
  messageKey: string;
  messageParams?: Record<string, string | number>;
}

export interface TimeSeriesParseResult {
  points: CreateTimeSeriesDataPointDto[];
  unknownHeaders: string[];
  inferredTypes: Record<string, TimeSeriesFieldDataType>;
  errors: TimeSeriesImportError[];
}

@Injectable({ providedIn: 'root' })
export class TimeSeriesImportService {
  private readonly fileImport = inject(FileImportService);
  private readonly validationService = inject(TimeSeriesValidationService);

  async parseFile(
    file: File,
    table: TimeSeriesTable,
    separator: CsvSeparator = ',',
    opts: { sheetIndex?: number } = {},
  ): Promise<TimeSeriesParseResult> {
    // raw: keep each cell's literal text. Without this, SheetJS fuzzy-parses
    // date-like strings itself — for an ambiguous dd/MM/yyyy value (both parts
    // <=12) it assumes US MM/dd order, silently swapping day and month before
    // our own dd/MM parsing (Utils.parseZonedDateTime) ever sees the string.
    // Unambiguous dates (day>12) were never affected, which is what made this
    // look like an inconsistent, per-row bug rather than a systemic one.
    const rows = await this.fileImport.readSheet(file, separator, {
      raw: true,
      sheetIndex: opts.sheetIndex,
    });

    if (rows.length < 2) {
      return {
        points: [],
        unknownHeaders: [],
        inferredTypes: {},
        errors: [{ row: 1, messageKey: 'error_file_empty' }],
      };
    }

    const fileHeaders: string[] = rows[0].map((h) => String(h ?? '').trim());

    // Match timestamp column
    const timestampIdx = fileHeaders.findIndex(
      (h) => h.toLowerCase() === table.timestampField.toLowerCase(),
    );

    // Build column-to-index map using importColumnName || label
    const columnMap: { key: string; idx: number; dataType: TimeSeriesFieldDataType }[] = [];
    const knownHeaders = new Set<string>();
    if (timestampIdx >= 0)
      knownHeaders.add(fileHeaders[timestampIdx].toLowerCase());

    for (const column of table.columns) {
      const headerName = (
        column.importColumnName || column.label
      ).toLowerCase();
      const idx = fileHeaders.findIndex((h) => h.toLowerCase() === headerName);
      if (idx >= 0) {
        columnMap.push({ key: column.key, idx, dataType: column.dataType });
        knownHeaders.add(fileHeaders[idx].toLowerCase());
      }
    }

    const unknownHeaders = fileHeaders.filter(
      (h) => h.trim() && !knownHeaders.has(h.toLowerCase()),
    );

    const unknownHeaderIndexMap: Record<string, number> = {};
    const unknownHeaderValues: Record<string, string[]> = {};
    for (const h of unknownHeaders) {
      const idx = fileHeaders.findIndex((fh) => fh.toLowerCase() === h.toLowerCase());
      unknownHeaderIndexMap[h] = idx;
      unknownHeaderValues[h] = [];
    }

    const points: CreateTimeSeriesDataPointDto[] = [];
    const errors: TimeSeriesImportError[] = [];

    const now = new Date();
    const startOfTomorrowUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];

      // Skip fully empty rows
      if (row.every((cell) => cell === '' || cell == null)) continue;

      const timestampCell = timestampIdx >= 0 ? row[timestampIdx] : '';
      const timestamp = String(timestampCell ?? '').trim();
      if (!timestamp) {
        // Row 2 (r === 1, immediately after the header) with an empty timestamp
        // but data in other cells is treated as a unit row (see export "Include
        // unit row" option) — skip it silently. Any other row missing a
        // timestamp is a real error.
        const isUnitRow =
          r === 1 &&
          row.some(
            (cell, i) => i !== timestampIdx && cell !== '' && cell != null,
          );
        if (!isUnitRow) {
          errors.push({
            row: r + 1,
            messageKey: 'error_missing_required_timestamp',
            messageParams: {
              column: table.timestampField,
            },
          });
        }
        continue;
      }

      const values: Record<string, string> = {};
      for (const { key, idx, dataType } of columnMap) {
        const val = row[idx];
        if (val !== '' && val != null) {
          const strVal = String(val);
          // A known number column should never receive a non-numeric string —
          // skip just this value rather than the whole row.
          if (dataType === 'number' && !isValidFiniteNumber(strVal)) continue;
          values[key] = strVal;
        }
      }

      for (const h of unknownHeaders) {
        const val = row[unknownHeaderIndexMap[h]];
        if (val !== '' && val != null) {
          unknownHeaderValues[h].push(String(val));
        }
      }

      const parsedTimestamp = Utils.parseZonedDateTime(
        timestampCell ?? '',
        table.timezone,
      );

      if (parsedTimestamp !== null && parsedTimestamp >= startOfTomorrowUtc) {
        errors.push({ row: r + 1, messageKey: 'error_future_timestamp' });
        continue;
      }

      const normalizedTimestamp = parsedTimestamp
        ? parsedTimestamp.toISOString()
        : timestamp;

      points.push({
        timestamp: normalizedTimestamp,
        values,
        status: this.validationService.inferStatusFromValues(
          table.columns,
          values,
        ),
      });
    }

    const inferredTypes: Record<string, TimeSeriesFieldDataType> = {};
    for (const h of unknownHeaders) {
      const vals = unknownHeaderValues[h];
      inferredTypes[h] =
        vals.length > 0 && vals.every((v) => isValidFiniteNumber(v))
          ? 'number'
          : 'unknown';
    }

    return { points, unknownHeaders, inferredTypes, errors };
  }

  /** Returns a new TimeSeriesTable with a column appended for each unknown
   *  header, keyed/typed from the parse result. Pure — the original table
   *  and its columns are never mutated. Returns the original table unchanged
   *  if there are no unknown headers. */
  buildTableWithUnknownColumns(
    table: TimeSeriesTable,
    unknownHeaders: string[],
    inferredTypes: Record<string, TimeSeriesFieldDataType> = {},
  ): TimeSeriesTable {
    if (!unknownHeaders.length) return table;

    const existingKeys = new Set(table.columns.map((column) => column.key));
    const addedColumns: TimeSeriesTableColumn[] = [];

    unknownHeaders.forEach((header, idx) => {
      const label = header.trim();
      if (!label) return;

      let baseKey = slugifyColumnKey(label);

      if (!baseKey) {
        baseKey = `column_${table.columns.length + idx + 1}`;
      }

      let key = baseKey;
      let suffix = 2;
      while (existingKeys.has(key)) {
        key = `${baseKey}_${suffix}`;
        suffix += 1;
      }
      existingKeys.add(key);

      addedColumns.push({
        key,
        label,
        importColumnName: label,
        dataType: inferredTypes[header] ?? 'unknown',
        required: false,
        unit: '',
        order: table.columns.length + addedColumns.length,
        visible: true,
      });
    });

    if (!addedColumns.length) return table;

    const dto = table.toDto();
    dto.fields = [
      ...dto.fields,
      ...addedColumns.map((column, index) => ({
        key: column.key,
        label: column.label,
        importColumnName: column.importColumnName,
        dataType: column.dataType,
        required: column.required,
        unit: column.unit,
        order: column.order ?? dto.fields.length + index,
        visible: column.visible,
      })),
    ];

    return TimeSeriesTable.fromDto(dto);
  }
}
